// Boot: the ride-out menu, the pause menu (per-player: your cowboy goes to the sky, the
// posse plays on), settings, and the render loop.

import { DualSense } from './feel/dualsense';
import { CLASSES, ClassId } from '../sim/defs';
import { DirState } from '../sim/game';
import { GameClient } from './game';
import { SFX } from './audio/sfx';
import type { Settings } from './input/input';
import { PadNav, psGlyph, type NavScope } from './input/padnav';
import { Sfx } from './audio/sfx';
import { loadStoredLoadout, openLoadout, saveLoadout, type LoadoutHandle } from './ui/loadout';
import { GRENADES, weaponDef } from '../sim/weapons';
import { Tracker } from './progress/ledger';
import { openRecord, type RecordHandle } from './progress/record';
import { requisitionToast } from './progress/toast';
import { earnedCount } from '../sim/progression';
import { RunStage } from '../sim/saloon';
import { paperwork } from './saloon/director';
import { DefaultLoadingManager } from 'three';

/** the title screen (client/title/title.html), painted before this bundle arrived */
interface TitleApi { active: boolean; onGo: ((kind: 'key' | 'mouse' | 'pad') => void) | null; progress(p: number): void; ready(): void; hide(): void }
const title = (window as unknown as { __title?: TitleApi }).__title;

const $ = <T extends HTMLElement = HTMLElement>(id: string) => document.getElementById(id) as T;
const params = new URLSearchParams(location.search);

function store<T>(key: string, fallback: T): T {
  try { const v = localStorage.getItem(key); return v ? { ...fallback, ...JSON.parse(v) } : fallback; } catch { return fallback; }
}
function save(key: string, v: unknown) { try { localStorage.setItem(key, JSON.stringify(v)); } catch { /* private mode */ } }
function getStr(key: string, fallback: string) { try { return localStorage.getItem(key) ?? fallback; } catch { return fallback; } }
function setStr(key: string, v: string) { try { localStorage.setItem(key, v); } catch { /* ignore */ } }

const settings: Settings = store('emg.settings', { sensitivity: 2.2, padSensitivity: 1.2, invertY: false, volume: 0.8, music: 0.8, rumble: 1, shake: 1, quality: 2, showNet: false });
if (params.has('q')) settings.quality = Number(params.get('q'));

const CLASS_BLURB = [
  'Pistol-whip to prime, headshot to detonate. Body shots waste the setup. Big Iron cracks armour.',
  'Fire-dash through robots to prime them, then shoot from the air to detonate. Glass shield.',
  'Backstab to prime, shoot from behind or from the smoke to detonate. Warp past bullets.',
  'Shield-bash to prime, charge to detonate. Huge shield, Auto MG, slow and loud.',
];

let cls: ClassId = Number(params.get('cls') ?? getStr('emg.cls', '0')) as ClassId;
const nameInput = $<HTMLInputElement>('name');
const roomInput = $<HTMLInputElement>('room');
// first visit: a cowboy name of your own, so two posse members never both read "Cowboy"
const NICKS = ['Dusty', 'Slim', 'Tex', 'Buck', 'Hoss', 'Rusty', 'Ace', 'Duke', 'Jesse', 'Wyatt', 'Boone', 'Cash'];
nameInput.value = params.get('name') ?? getStr('emg.name', NICKS[Math.floor(Math.random() * NICKS.length)]);
const newCode = () => Math.random().toString(36).slice(2, 7);
roomInput.value = params.get('room') ?? newCode();
// arriving by invite link: say whose posse you're joining instead of a bare "Ride out"
if (params.get('room')) $('ride').lastChild!.textContent = `Join posse ${params.get('room')}`;

function kitLine(c: ClassId) {
  const l = loadStoredLoadout(c);
  return `${weaponDef(l.primary).name} + ${weaponDef(l.secondary).name}`;
}

function renderClasses(host: HTMLElement, mini: boolean, onPick: (c: ClassId) => boolean | void, locked = false) {
  host.innerHTML = '';
  CLASSES.forEach((c, i) => {
    const d = document.createElement('div');
    d.className = 'cls' + (i === cls ? ' sel' : '') + (locked && i !== cls ? ' locked' : '');
    d.innerHTML = mini ? `<h3>${c.name}</h3>` : `<h3>${c.name}</h3><div class="role">${c.role} · ${kitLine(i as ClassId)}</div><p>${CLASS_BLURB[i]}</p>`;
    d.onclick = () => { if (locked || onPick(i as ClassId) === false) return; cls = i as ClassId; renderClasses(host, mini, onPick, locked); };
    host.appendChild(d);
  });
}
renderClasses($('classes'), false, () => {});

// static PS5 glyph slots in the page
document.querySelectorAll<HTMLElement>('[data-glyph]').forEach((el) => { el.innerHTML = psGlyph(el.dataset.glyph as 'cross'); });

const KBM_CONTROLS = [
  '<b>WASD</b> move · <b>Mouse</b> aim · <b>Click</b> fire · <b>Right-click</b> aim · <b>R</b> reload (costs heat) · <b>F</b> melee (your primer)',
  '<b>G</b> grenade · <b>X</b> or <b>wheel</b> swap guns · <b>T</b> special weapon (from crates) · <b>E</b> grab a crate · <b>B</b> use power-up · <b>H</b> drink whiskey (heals)',
  '<b>Space</b> jump, again in the air to fly (again to drop) · <b>Shift</b> sprint, or hover in the air · <b>C</b> roll (slide from a sprint, dive in the air, puts out fire) · <b>Q</b> class dash',
  '<b>E</b> hold to revive · <b>Z</b> tap to ping, hold for quick chat · <b>Esc</b> pause (you go up to the sky, the posse plays on) · <b>Tab</b> posse · <b>F3</b> net graph · pads work too',
].join('<br>');
const pill = (t: string) => `<span class="pill">${t}</span>`;
const PAD_CONTROLS = [
  `${pill('L')} move · ${pill('L3')} sprint · ${pill('R')} aim · ${pill('R2')} fire · ${pill('L2')} aim down sights · ${psGlyph('square')} reload (costs heat) · ${pill('R3')} melee (your primer)`,
  `${pill('R1')} grenade · ${psGlyph('triangle')} swap guns · ${pill('D-PAD ◀')} special weapon (from crates) · ${psGlyph('square')} grab a crate · ${pill('D-PAD ↓')} use power-up · ${pill('D-PAD →')} drink whiskey (heals)`,
  `${psGlyph('cross')} jump, again in the air to fly, again to drop · ${pill('L1')} hover on / off, guns out · ${psGlyph('circle')} roll (slide from a sprint, dive in the air, puts out fire) · ${psGlyph('circle')}${psGlyph('circle')} class dash`,
  `${psGlyph('square')} hold to revive · ${pill('D-PAD ▲')} tap to ping, hold for quick chat · ${pill('OPTIONS')} pause (you go up to the sky, the posse plays on) · ${pill('CREATE')} posse`,
].join('<br>');
function renderControls() {
  const html = nav.device === 'pad' ? PAD_CONTROLS : KBM_CONTROLS;
  $('controls').innerHTML = html;
  $('howto-controls').innerHTML = html;
}

// ---- controller navigation for the menus
const nav = new PadNav();
const lobbyScope: NavScope = {
  root: $('menu'),
  selector: '#classes .cls, #name, #room, #ride, #gear, #record, #copy',
  initial: () => $('ride'),
  onBack: () => {
    const a = document.activeElement as HTMLElement | null;
    if (a && a.tagName === 'INPUT') a.blur(); else nav.focus($('ride'));
  },
  // picking a class moves straight on to Ride Out, so it's ✕, ✕ from a cold start
  afterActivate: (el) => { if (el.classList.contains('cls')) nav.focus($('ride')); },
};
const pauseScope: NavScope = {
  root: $('pause'),
  selector: '#resume, #pause-record, #howto summary, #pause-classes .cls, #pause input[type=range], #pause input[type=checkbox], #invite, #leave',
  initial: () => $('resume'),
  onBack: () => setPaused(false),
};
nav.onDevice = (d) => {
  renderControls();
  if (game) game.input.glyphs = d;
};
nav.onPadSeen = () => renderControls();
renderControls();
nav.open(lobbyScope);
// a gamepad press isn't a user gesture, so audio may stay locked until a key/click: retry on those
// (the first gesture also starts the saloon music under the menu)
const regesture = () => { SFX.volume = settings.volume; SFX.musicVolume = settings.music ?? 0.8; SFX.unlock(); if (!game) SFX.setMusic('saloon'); };
window.addEventListener('keydown', regesture);
window.addEventListener('pointerdown', regesture);
// menu sounds: pad moves tick, ✕ picks, ○ backs out; a mouse press on a menu button picks
nav.onMoved = () => SFX.play('ui_tick');
nav.onActivated = () => SFX.play('ui_pick');
nav.onBacked = () => SFX.play('ui_back');
document.addEventListener('pointerdown', (e) => { if ((e.target as HTMLElement | null)?.closest?.('button, .cls')) SFX.play('ui_pick'); });

function inviteUrl() {
  const u = new URL(location.href);
  u.search = '';
  if (!roomInput.value.trim()) roomInput.value = newCode();
  u.searchParams.set('room', roomInput.value.trim());
  if (params.get('server')) u.searchParams.set('server', params.get('server')!);
  return u.toString();
}
function toast(msg: string) {
  const t = $('toast');
  t.textContent = msg; t.classList.remove('hidden');
  setTimeout(() => t.classList.add('hidden'), 1800);
}
async function copyInvite() {
  try { await navigator.clipboard.writeText(inviteUrl()); toast('Invite link copied'); } catch { toast(inviteUrl()); }
}
$('copy').onclick = copyInvite;
$('invite').onclick = copyInvite;

function serverUrl(room: string) {
  const explicit = params.get('server');
  let base: string;
  if (explicit) base = explicit;
  else if (location.port === '5173' || location.port === '4173') base = `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.hostname}:8787/ws`;
  else base = `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/ws`;
  const u = new URL(base);
  u.searchParams.set('room', room);
  return u.toString();
}

let game: GameClient | null = null;
/** per-frame check while the title screen is up (holdForTitle) */
let titleTick: (() => void) | null = null;

function start() {
  const name = nameInput.value.trim() || 'Cowboy';
  // never fall back to a shared public room: strangers would land in each other's posse
  const room = (roomInput.value.trim().toLowerCase().replace(/[^a-z0-9-]/g, '')) || newCode();
  roomInput.value = room;
  setStr('emg.name', name);
  setStr('emg.cls', String(cls));
  history.replaceState(null, '', `?room=${room}${params.get('server') ? `&server=${encodeURIComponent(params.get('server')!)}` : ''}`);
  const lag = { lag: Number(params.get('lag') ?? 0), jitter: Number(params.get('jitter') ?? 0), loss: Number(params.get('loss') ?? 0) };
  const token = getStr(`emg.token.${room}`, '');
  const canvas = $<HTMLCanvasElement>('game');
  game = new GameClient(canvas, settings, serverUrl(room), name, cls, token, lag);
  game.setLoadout(loadStoredLoadout(cls));
  game.progress = tracker;
  game.setLook(tracker.wire());
  game.showNet = settings.showNet || params.has('net');
  game.sfx.unlock();
  game.sfx.volume = settings.volume;
  game.sfx.musicVolume = settings.music ?? 0.8;
  game.input.rumbleScale = settings.rumble ?? 1;
  game.rig.shakeScale = settings.shake ?? 1;
  game.sfx.play('ride_out');
  game.input.glyphs = nav.device;
  game.onDisconnect = (r) => {
    // a dropped socket (a deploy restarts the server, a wifi blip) dials straight back in; the
    // old lobby only comes back for a posse that's full
    if (!/full/i.test(r) && game) { rejoin.lost(game); return; }
    rejoin.stop();
    title?.hide();
    $('menu').classList.remove('hidden');
    $('pause').classList.add('hidden');
    nav.open(lobbyScope);
    $('err').textContent = /full/i.test(r)
      ? 'That posse is full (four of four). Ask them for a different code, or start your own.'
      : `Lost the server: ${r}. Ride out again to rejoin your slot.`;
    game = null;
    SFX.stopAllLoops();
    SFX.setMusic('saloon');
  };
  game.onClassPick = (c) => { cls = c; setStr('emg.cls', String(c)); };
  game.onRejoin = () => rejoin.back();
  game.onRoster = () => { if (game?.session.token) setStr(`emg.token.${room}`, game.session.token); record?.refresh(); };
  wireGame(game);
  $('menu').classList.add('hidden');
  nav.open(null);
  // pointer lock is for the mouse only: a pad plays fine without it (and can't grant it anyway)
  if (!params.has('auto') && nav.device !== 'pad') game.input.lock();
  loop();
}

/** Lost the server: a small notice over the game while it dials back in with the slot token.
 *  Backs off 0.5 s → 8 s and keeps trying; ○ or a click hides the notice (the retries go on). */
const rejoin = (() => {
  let el: HTMLDivElement | null = null, timer = 0, tries = 0, hidden = false, g: GameClient | null = null, padWas = false;
  const show = (html: string) => {
    if (!el) {
      el = document.createElement('div'); el.id = 'rejoin';
      el.onclick = () => { hidden = true; el?.classList.add('hidden'); };
      document.body.appendChild(el);
    }
    el.innerHTML = html;
    el.classList.toggle('hidden', hidden);
  };
  const dial = () => {
    if (!g || g.session.connected) return;
    tries++;
    show(`<b>Lost the server</b> · reconnecting${tries > 1 ? ` (try ${tries})` : ''}… <span class="rj-hide">${psGlyph('circle')} hide</span>`);
    g.session.reconnect();
    timer = window.setTimeout(dial, Math.min(8000, 500 * 2 ** Math.min(tries, 4)));
  };
  // watchdog: a socket that goes silent without closing (a sleeping laptop, a stuck path) is dead too
  let lastCheck = performance.now();
  window.setInterval(() => {
    // a long frame here (a load hitch) froze this check too: the socket's queued messages haven't been
    // read yet, so silence measured across the freeze isn't the server's. Skip a beat.
    const now = performance.now(), frozen = now - lastCheck > 1000;
    lastCheck = now;
    if (!frozen && game && game.session.connected && game.session.lastMsgAt && now - game.session.lastMsgAt > 6000) {
      game.session.drop('went silent');
    }
    game?.session.heartbeat();
    const pad = navigator.getGamepads?.().some((p) => p?.buttons[1]?.pressed) ?? false;
    if (pad && !padWas && el && !el.classList.contains('hidden')) { hidden = true; el.classList.add('hidden'); }
    padWas = pad;
  }, 250);
  return {
    lost(game: GameClient) {
      if (g === game && timer) return;
      g = game; tries = 0; hidden = false;
      clearTimeout(timer);
      timer = window.setTimeout(dial, 400);
      show(`<b>Lost the server</b> · reconnecting… <span class="rj-hide">${psGlyph('circle')} hide</span>`);
    },
    back() {
      if (!g) return;
      clearTimeout(timer); timer = 0; g = null; hidden = false;
      show('<b>Back in</b> · reconnected');
      window.setTimeout(() => { if (!g) el?.classList.add('hidden'); }, 2200);
    },
    stop() { clearTimeout(timer); timer = 0; g = null; el?.classList.add('hidden'); },
  };
})();

function wireGame(g: GameClient) {
  g.input.onToggleMenu = () => { if (!rack?.open && !record?.open) setPaused(!g.input.paused); };
  g.input.onToggleNet = () => { g.showNet = !g.showNet; settings.showNet = g.showNet; save('emg.settings', settings); syncSettings(); };
  g.input.onTogglePosse = (show) => { g.showPosse = show; };
}

function setPaused(p: boolean) {
  if (!game) return;
  game.input.paused = p;
  $('pause').classList.toggle('hidden', !p);
  game.sfx.duck(p ? 0.4 : 1);
  if (p) {
    if (game.session.me) cls = game.session.me.cls as ClassId; // the gun rack may have swapped it
    $('pause-code').textContent = new URLSearchParams(location.search).get('room') ?? '';
    const howto = () => { const c = CLASSES[cls]; $('howto-class').textContent = `As ${c.name}: ${c.primer.toLowerCase()} to prime, ${c.detonator.toLowerCase()} to detonate.`; };
    howto();
    // kit swaps are a between-waves thing (the server refuses them mid-wave too)
    const locked = kitLocked();
    $('pause-kit').textContent = locked ? 'Swap kit · locked until the wave is clear' : 'Swap kit';
    game.input.unlock(); renderClasses($('pause-classes'), true, (c) => { if (kitLocked()) { toast('Kit locked until the wave is clear'); return false; } cls = c; game?.session.setClass(c); game?.setLoadout(loadStoredLoadout(c)); howto(); }, locked); syncSettings(); nav.open(pauseScope); }
  else {
    nav.open(null);
    game.input.swallowPad(); // the ✕ / ○ that closed the menu doesn't also jump / dodge
    if (nav.device !== 'pad') game.input.lock();
  }
}
// pad unplugged or out of battery mid-fight: go up to the sky rather than stand there
window.addEventListener('gamepaddisconnected', () => { if (game && !game.input.paused && nav.device === 'pad') setPaused(true); });
$('resume').onclick = () => setPaused(false);

/** Kit and gun-rack changes wait for a breather (the server refuses them mid-wave too). */
function kitLocked() { return game?.session.latest?.dirState === DirState.Wave; }

// ---- the gun rack: pick primary, secondary and grenade (saved per class, sent to the server)
let rack: LoadoutHandle | null = null;
let menuSfx: Sfx | null = null; // the lobby has no game yet: a small bank just for menu clicks
function openRack(back: NavScope, afterClose: () => void) {
  if (rack?.open) return;
  nav.open(null);
  const sfx = game?.sfx ?? (menuSfx ??= new Sfx());
  sfx.unlock(); sfx.volume = settings.volume;
  rack = openLoadout(document.body, loadStoredLoadout(cls), (l) => {
    saveLoadout(cls, l);
    game?.setLoadout(l);
    toast(`${weaponDef(l.primary).name} + ${weaponDef(l.secondary).name} + ${GRENADES[l.grenade].name}`);
    rack = null; afterClose(); nav.open(back);
  }, () => { rack = null; afterClose(); nav.open(back); }, { cls, sound: (n) => sfx.play(n) });
}
$('gear').onclick = () => openRack(lobbyScope, () => { renderClasses($('classes'), false, () => {}); nav.focus($('ride')); });
$('leave').onclick = () => { location.href = location.pathname; };

// ---- the Service Record: counts every run on this browser, pays commendations, holds your look
const tracker = new Tracker();
tracker.onEarned = (c) => requisitionToast(c, earnedCount(tracker.ledger));
tracker.onLook = (w) => game?.setLook(w);
let record: RecordHandle | null = null;
function openRecordScreen(back: NavScope | null, afterClose: () => void) {
  if (record?.open || rack?.open) return;
  nav.open(null);
  const g = game;
  // in a posse the contracts are signable (saloon only); from the lobby they're read-only
  const posse = g ? {
    mask: () => g.posseContracts(),
    current: () => g.contract,
    canSign: () => g.runStage === RunStage.Saloon,
    sign: (k: number) => g.signContract(k),
  } : undefined;
  if (g) g.onContract = () => record?.refresh();
  record = openRecord(document.body, tracker, nameInput.value.trim() || 'Cowboy', () => { record = null; afterClose(); nav.open(back); }, posse);
}
// Pop's "Show me my paperwork." in the saloon
paperwork.open = (onClose) => { openRecordScreen(null, onClose); if (!record) onClose(); };
$('record').onclick = () => openRecordScreen(lobbyScope, () => nav.focus($('record')));
$('pause-record').onclick = () => openRecordScreen(pauseScope, () => {});

function syncSettings() {
  const bind = (id: string, key: keyof Settings, fmt: (v: number) => string) => {
    const el = $<HTMLInputElement>(id);
    el.value = String(settings[key]);
    $(id + '-v').textContent = fmt(Number(settings[key]));
    el.oninput = () => {
      (settings as unknown as Record<string, number>)[key] = Number(el.value);
      $(id + '-v').textContent = fmt(Number(el.value));
      save('emg.settings', settings);
      if (key === 'volume') SFX.volume = settings.volume;
      if (key === 'music') SFX.musicVolume = settings.music ?? 0.8;
      if (key === 'rumble' && game) { game.input.rumbleScale = settings.rumble ?? 1; game.input.paused = false; game.input.rumble(0.5, 0.5, 120); game.input.paused = true; }
      if (key === 'shake' && game) { game.rig.shakeScale = settings.shake ?? 1; if (game.feel) game.feel.shakeScale = settings.shake ?? 1; } // the feel lane's lens punch scales with it too
      if (key === 'quality' && game) { game.stage.post.scale = [0.6, 0.8, 1, 1][settings.quality]; game.stage.resize(); toast('Shadows and grass density apply on the next ride'); }
    };
  };
  bind('sens', 'sensitivity', (v) => v.toFixed(1));
  bind('psens', 'padSensitivity', (v) => v.toFixed(1));
  bind('vol', 'volume', (v) => `${Math.round(v * 100)}%`);
  bind('mus', 'music', (v) => `${Math.round(v * 100)}%`);
  bind('rum', 'rumble', (v) => (v <= 0 ? 'OFF' : `${Math.round(v * 100)}%`));
  bind('shk', 'shake', (v) => (v <= 0 ? 'OFF' : `${Math.round(v * 100)}%`));
  bind('qual', 'quality', (v) => ['LOW', 'MED', 'HIGH', 'ULTRA'][v]);
  // DualSense adaptive triggers: WebHID needs a click (a pad button isn't a user gesture to Chrome)
  const trig = $<HTMLInputElement>('trig');
  $('trig-row').style.display = DualSense.supported() ? '' : 'none';
  const pad = game?.feel?.pad;
  trig.checked = !!pad?.on;
  $('trig-v').textContent = pad?.on ? 'ON' : '';
  if (pad) pad.onChange = (on) => { trig.checked = on; $('trig-v').textContent = on ? 'ON' : ''; settings.triggers = on; save('emg.settings', settings); };
  trig.onchange = async () => {
    const p = game?.feel?.pad;
    if (!p) { trig.checked = false; return; }
    if (trig.checked) { const ok = await p.connect(); trig.checked = ok; if (!ok) toast('No DualSense picked (Chrome or Edge, USB or Bluetooth)'); }
    else await p.disconnect();
  };
  const inv = $<HTMLInputElement>('inv');
  inv.checked = settings.invertY;
  inv.onchange = () => { settings.invertY = inv.checked; save('emg.settings', settings); };
  const net = $<HTMLInputElement>('net');
  net.checked = settings.showNet;
  net.onchange = () => { settings.showNet = net.checked; if (game) game.showNet = net.checked; save('emg.settings', settings); };
}

let raf = 0;
function loop() {
  cancelAnimationFrame(raf);
  const tick = () => {
    if (game) game.frame();
    titleTick?.();
    raf = requestAnimationFrame(tick);
  };
  raf = requestAnimationFrame(tick);
}

$('ride').onclick = start;
nameInput.onkeydown = roomInput.onkeydown = (e) => { if (e.key === 'Enter') start(); };
// the site opens straight into the saloon: no menu in front of it (?menu brings the old one back).
// Name, invite link and class live in the pause menu (Options); the gear and paperwork are at the bar.
if (params.has('auto') || !params.has('menu')) {
  start();
  if (title?.active && game) holdForTitle(game);
  else if (!params.has('auto')) soundHint();
} else $('menu').classList.remove('hidden');

/** A pad press doesn't count as a user gesture, so the browser keeps sound locked until a click or key. */
function soundHint() {
  const hint = document.createElement('div');
  hint.id = 'sound-hint';
  hint.textContent = 'Click anywhere to turn on the sound';
  document.body.appendChild(hint);
  const clear = () => { hint.remove(); game?.sfx.unlock(); window.removeEventListener('pointerdown', clear); window.removeEventListener('keydown', clear); };
  window.addEventListener('pointerdown', clear);
  window.addEventListener('keydown', clear);
}

// ---- the title screen: the game boots behind it (connects, loads the saloon, compiles shaders) with
// its input held off; the bar tracks the loads, and "mash any button" appears once the saloon is drawn
function holdForTitle(g: GameClient) {
  const t = title!;
  g.input.blocked = true;
  const prev = DefaultLoadingManager.onProgress;
  DefaultLoadingManager.onProgress = (url, loaded, total) => { prev?.(url, loaded, total); t.progress(0.2 + 0.6 * loaded / Math.max(1, total)); };
  let saloonIn = false, watching = false, frames = 0;
  titleTick = () => {
    if (!t.active) { titleTick = null; return; }
    if (g.session.me) t.progress(0.85);
    const sal = g.saloon;
    if (sal && !watching) { watching = true; void Promise.all([sal.car.ready, sal.pop.ready]).catch(() => {}).then(() => { saloonIn = true; }); }
    // a few drawn frames after the saloon lands, so its shaders are compiled before the reveal
    if (saloonIn && g.session.me && ++frames > 20) t.ready();
  };
  setTimeout(() => t.ready(), 30000); // never trap anyone behind the title
  t.onGo = (kind) => {
    // a key or click is a real gesture: take the mouse now and wake the sound on the stamp
    if (kind !== 'pad') g.input.lock();
    SFX.volume = settings.volume; SFX.unlock();
    g.sfx.play('ride_out');
    // let go of the input once the button that dismissed the title is old news
    setTimeout(() => { g.input.blocked = false; }, 350);
    if (kind === 'pad') soundHint();
  };
}
// for headless tests
(window as unknown as Record<string, unknown>).__emg = () => game;
